// ============================================================================
// Vocabularios de Activos (épica común CC-CONSOLE ↔ WAYAK, CC-CONSOLE #1638)
// ============================================================================
// Las claves (en español, sin acentos) son las que se guardan en Firestore; las
// etiquetas son solo para mostrar. Nunca se renombra una clave: se agrega una
// nueva y se migra.
// ============================================================================

export const TORRE_ASSETS_VERSION = "0.1.0";

export type Label = { es: string; en: string };

// --- Colecciones (todas con `company_id`, solo las lee y escribe el API) ------
export const ASSETS_COLLECTION = "assets";
/** Candado de unicidad de la serie: `{companyId}_{identifierNorm}` → `{ assetId }`. */
export const ASSET_IDENTIFIERS_COLLECTION = "asset_identifiers";
export const ASSET_EVENTS_COLLECTION = "asset_events";
export const ASSET_PURCHASES_COLLECTION = "asset_purchases";
/** No se usa `providers`: ese nombre ya es el catálogo de adaptadores telemáticos. */
export const ASSET_SUPPLIERS_COLLECTION = "asset_suppliers";
export const ASSET_MAINTENANCE_COLLECTION = "asset_maintenance";
/** Un documento por compañía: `asset_settings/{companyId}`. */
export const ASSET_SETTINGS_COLLECTION = "asset_settings";

/** Las 7 colecciones de Activos (reglas `if false`, índices, pruebas). */
export const ASSET_COLLECTIONS = [
  ASSETS_COLLECTION,
  ASSET_IDENTIFIERS_COLLECTION,
  ASSET_EVENTS_COLLECTION,
  ASSET_PURCHASES_COLLECTION,
  ASSET_SUPPLIERS_COLLECTION,
  ASSET_MAINTENANCE_COLLECTION,
  ASSET_SETTINGS_COLLECTION,
] as const;

export const ASSETS_TIMEZONE = "America/Mexico_City";
/** Moneda base de los montos `*Base` (la misma que `@torre/expenses`). */
export const BASE_CURRENCY = "MXN";

// --- Llaves del catálogo de derechos (viven en `@torre/entitlements`, v26) ----
// Aquí solo se nombran para que el API y la pantalla no escriban el texto a
// mano. La fuente de verdad sigue siendo el catálogo.
export const ASSETS_APP_KEY = "app:activos";
export const ASSETS_MENU_KEYS = {
  inventario: "menu:activos.inventario",
  compras: "menu:activos.compras",
  proveedores: "menu:activos.proveedores",
} as const;
/** Ver y capturar costos, y ver el estado de finanzas. */
export const ASSETS_COSTS_FEATURE_KEY = "feat:activos.costos";

// --- Categorías (vocabulario fijo + «otro», D10) ------------------------------
// No hay GPS, SIM ni vehículos: esos viven en `devices` y `vehicles` (D1).
export const ASSET_CATEGORIES = [
  "computo",
  "telefonia",
  "mobiliario",
  "herramienta",
  "maquinaria",
  "equipo_oficina",
  "otro",
] as const;
export type AssetCategory = (typeof ASSET_CATEGORIES)[number];

export const ASSET_CATEGORY_LABELS: Record<AssetCategory, Label> = {
  computo: { es: "Cómputo", en: "Computers" },
  telefonia: { es: "Telefonía", en: "Phones" },
  mobiliario: { es: "Mobiliario", en: "Furniture" },
  herramienta: { es: "Herramienta", en: "Tools" },
  maquinaria: { es: "Maquinaria", en: "Machinery" },
  equipo_oficina: { es: "Equipo de oficina", en: "Office equipment" },
  otro: { es: "Otro", en: "Other" },
};

// --- Estado del activo --------------------------------------------------------
export const ASSET_STATUSES = ["en_almacen", "en_uso", "en_mantenimiento", "baja"] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

export const ASSET_STATUS_LABELS: Record<AssetStatus, Label> = {
  en_almacen: { es: "En almacén", en: "In stock" },
  en_uso: { es: "En uso", en: "In use" },
  en_mantenimiento: { es: "En mantenimiento", en: "In maintenance" },
  baja: { es: "De baja", en: "Retired" },
};

/** Estados que cuentan como inventario vivo (valor total, tarjetas). */
export const LIVE_ASSET_STATUSES: readonly AssetStatus[] = ["en_almacen", "en_uso", "en_mantenimiento"];

// --- Origen del registro (el MVP solo escribe `manual`) ------------------------
export const ASSET_SOURCES = ["manual", "import", "factura", "migracion"] as const;
export type AssetSource = (typeof ASSET_SOURCES)[number];

export const ASSET_SOURCE_LABELS: Record<AssetSource, Label> = {
  manual: { es: "Alta manual", en: "Manual entry" },
  import: { es: "Importación CSV", en: "CSV import" },
  factura: { es: "Desde factura", en: "From invoice" },
  migracion: { es: "Migración", en: "Migration" },
};

// --- Motivos de baja (no hay DELETE) ---------------------------------------------
export const RETIRE_REASONS = ["vendido", "danado", "extraviado", "donado"] as const;
export type RetireReason = (typeof RETIRE_REASONS)[number];

export const RETIRE_REASON_LABELS: Record<RetireReason, Label> = {
  vendido: { es: "Vendido", en: "Sold" },
  danado: { es: "Dañado", en: "Damaged" },
  extraviado: { es: "Extraviado", en: "Lost" },
  donado: { es: "Donado", en: "Donated" },
};

// --- Línea de tiempo (`asset_events`, solo se agregan registros) ---------------
export const ASSET_EVENT_TYPES = [
  "alta",
  "edicion",
  "asignacion",
  "devolucion",
  "mantenimiento_abierto",
  "mantenimiento_cerrado",
  "baja",
  "finanzas",
] as const;
export type AssetEventType = (typeof ASSET_EVENT_TYPES)[number];

export const ASSET_EVENT_TYPE_LABELS: Record<AssetEventType, Label> = {
  alta: { es: "Alta", en: "Created" },
  edicion: { es: "Edición", en: "Edited" },
  asignacion: { es: "Asignación", en: "Assigned" },
  devolucion: { es: "Devolución al almacén", en: "Returned to stock" },
  mantenimiento_abierto: { es: "Mantenimiento abierto", en: "Maintenance opened" },
  mantenimiento_cerrado: { es: "Mantenimiento cerrado", en: "Maintenance closed" },
  baja: { es: "Baja", en: "Retired" },
  finanzas: { es: "Finanzas", en: "Finance" },
};

// --- Mantenimiento simple (D7) -------------------------------------------------
export const MAINTENANCE_KINDS = ["preventivo", "correctivo"] as const;
export type MaintenanceKind = (typeof MAINTENANCE_KINDS)[number];

export const MAINTENANCE_KIND_LABELS: Record<MaintenanceKind, Label> = {
  preventivo: { es: "Preventivo", en: "Preventive" },
  correctivo: { es: "Correctivo", en: "Corrective" },
};

export const MAINTENANCE_STATUSES = ["abierto", "cerrado"] as const;
export type MaintenanceStatus = (typeof MAINTENANCE_STATUSES)[number];

export const MAINTENANCE_STATUS_LABELS: Record<MaintenanceStatus, Label> = {
  abierto: { es: "Abierto", en: "Open" },
  cerrado: { es: "Cerrado", en: "Closed" },
};

// --- Proveedores ------------------------------------------------------------------
export const SUPPLIER_ROLES = ["vende", "repara"] as const;
export type SupplierRole = (typeof SUPPLIER_ROLES)[number];

export const SUPPLIER_ROLE_LABELS: Record<SupplierRole, Label> = {
  vende: { es: "Vende", en: "Sells" },
  repara: { es: "Repara", en: "Repairs" },
};

// --- Integración con Finanzas (sección 3 del diseño; se escribe en el PR 4) -------
export const FINANCE_STATUSES = ["pendiente", "ligado", "desligado", "no_aplica"] as const;
export type FinanceStatus = (typeof FINANCE_STATUSES)[number];

export const FINANCE_STATUS_LABELS: Record<FinanceStatus, Label> = {
  pendiente: { es: "Pendiente", en: "Pending" },
  ligado: { es: "Ligado a Finanzas", en: "Linked to Finance" },
  desligado: { es: "Desligado de Finanzas", en: "Unlinked from Finance" },
  no_aplica: { es: "No aplica", en: "Not applicable" },
};

/** `asset_settings.financeSync`: `off` por omisión; `borrador` crea gastos `por_revisar`. */
export const FINANCE_SYNC_MODES = ["off", "borrador"] as const;
export type FinanceSyncMode = (typeof FINANCE_SYNC_MODES)[number];

/** Categorías de `@torre/expenses` por omisión (no se importa el paquete: cero dependencias). */
export const DEFAULT_EXPENSE_CATEGORY = "hardware";
export const DEFAULT_MAINTENANCE_EXPENSE_CATEGORY = "servicios";

// --- Depreciación -------------------------------------------------------------------
export const DEPRECIATION_METHODS = ["linea_recta"] as const;
export type DepreciationMethod = (typeof DEPRECIATION_METHODS)[number];

// --- Adjuntos -------------------------------------------------------------------------
export const ATTACHMENT_KINDS = ["factura_pdf", "factura_xml", "foto", "otro"] as const;
export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number];

export const ATTACHMENT_KIND_LABELS: Record<AttachmentKind, Label> = {
  factura_pdf: { es: "Factura (PDF)", en: "Invoice (PDF)" },
  factura_xml: { es: "Factura (XML)", en: "Invoice (XML)" },
  foto: { es: "Foto", en: "Photo" },
  otro: { es: "Otro", en: "Other" },
};

// --- Códigos de error que comparten el API y la pantalla ----------------------------
export const ASSET_ERROR_CODES = [
  /** 409: la serie ya es de otro activo de la compañía (`{ assetId }`). */
  "IDENTIFICADOR_DUPLICADO",
  /** 409: la serie o el IMEI ya es un dispositivo de rastreo (`{ deviceId }`). */
  "ES_DISPOSITIVO",
  /** 409: la factura ya está registrada como otra compra (`{ purchaseId }`). */
  "COMPRA_DUPLICADA",
  /** 409: el RFC ya es de otro proveedor de la compañía (`{ supplierId }`). */
  "PROVEEDOR_DUPLICADO",
  /** 409: la acción no se puede hacer en el estado actual del activo. */
  "ESTADO_INVALIDO",
  /** 422: el cuerpo no pasa las validaciones. */
  "DATOS_INVALIDOS",
  /** 403: la configuración de Activos es sólo de administradores (compañía o plataforma). */
  "ADMIN_REQUERIDO",
  /** 409: Finanzas está apagada en esta plataforma (WAYAK): no se puede encender la integración. */
  "FINANZAS_APAGADA",
  /** 403: D2, donde Finanzas es interna (CC-CONSOLE) sólo se integra la compañía de Gastos. */
  "FINANZAS_NO_PERMITIDA",
] as const;
export type AssetErrorCode = (typeof ASSET_ERROR_CODES)[number];

// --- Guardas -------------------------------------------------------------------------
const isOneOf =
  <T extends string>(list: readonly T[]) =>
  (v: unknown): v is T =>
    typeof v === "string" && (list as readonly string[]).includes(v);

export const isAssetCategory = isOneOf(ASSET_CATEGORIES);
export const isAssetStatus = isOneOf(ASSET_STATUSES);
export const isAssetSource = isOneOf(ASSET_SOURCES);
export const isRetireReason = isOneOf(RETIRE_REASONS);
export const isAssetEventType = isOneOf(ASSET_EVENT_TYPES);
export const isMaintenanceKind = isOneOf(MAINTENANCE_KINDS);
export const isMaintenanceStatus = isOneOf(MAINTENANCE_STATUSES);
export const isSupplierRole = isOneOf(SUPPLIER_ROLES);
export const isFinanceStatus = isOneOf(FINANCE_STATUSES);
export const isFinanceSyncMode = isOneOf(FINANCE_SYNC_MODES);
export const isAttachmentKind = isOneOf(ATTACHMENT_KINDS);
